import { useState, useEffect } from "react";
import api from "../../../utils/api";
import {
  formatDateForAPI,
  getLocalDateString,
  normalizeOrderType,
  formatOrderType,
} from "../utils/orderUtils";
import { ORDER_TYPE_ALL } from "../utils/constants";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { dateFromYmd, getRestaurantTodayDate } from "../../../utils/dateTimeUtils";

export const useOrdersHistory = () => {
  const { timezone } = useRestaurantConfig();
  const today = dateFromYmd(getRestaurantTodayDate(timezone));

  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [otherAppsDailySales, setOtherAppsDailySales] = useState([]);
  const [allConfiguredApps, setAllConfiguredApps] = useState([]);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [showAllDates, setShowAllDates] = useState(false);

  const [orderType, setOrderType] = useState("");
  const [orderTypeOptions, setOrderTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Generate order type options
  useEffect(() => {
    const types = Array.from(
      new Set(
        orders.map((o) => normalizeOrderType(o.orderType)).filter(Boolean),
      ),
    );

    const options = [
      ORDER_TYPE_ALL,
      ...types.map((t) => ({
        label: formatOrderType(t),
        value: t,
      })),
    ];

    setOrderTypeOptions(options);
  }, [orders]);

  // Fetch orders and other apps sales together when date filters change
  useEffect(() => {
    const loadAllData = async () => {
      setIsLoading(true);
      await Promise.all([fetchOrders(), fetchOtherAppsSales(), fetchAllConfiguredApps()]);
      setIsLoading(false);
    };

    loadAllData();
  }, [fromDate, toDate, showAllDates]);

  // Apply filters when any filter changes
  useEffect(() => {
    applyFilters();
  }, [orders, search, status, fromDate, toDate, showAllDates, orderType]);

  const fetchOrders = async () => {
    try {
      let endpoint = "/orders/history";

      if (!showAllDates && fromDate && toDate) {
        endpoint = `/orders/history?startDate=${formatDateForAPI(
          fromDate,
        )}&endDate=${formatDateForAPI(toDate)}`;
      }

      const res = await api.get(endpoint);
      setOrders(res.data || []);
    } catch (err) {
      console.error("Fetch orders error:", err);
      setOrders([]);
    }
  };

  const fetchOtherAppsSales = async () => {
    try {
      let endpoint = "/other-apps/daily-sales";

      if (!showAllDates && fromDate && toDate) {
        endpoint = `/other-apps/daily-sales?startDate=${formatDateForAPI(
          fromDate,
        )}&endDate=${formatDateForAPI(toDate)}`;
      }

      const res = await api.get(endpoint);
      setOtherAppsDailySales(res.data?.dailySales || []);
    } catch (err) {
      console.error("Fetch other apps sales error:", err);
      setOtherAppsDailySales([]);
    }
  };

  const fetchAllConfiguredApps = async () => {
    try {
      if (allConfiguredApps.length === 0) {
        const res = await api.get("/other-apps");
        setAllConfiguredApps(res.data?.apps || []);
      }
    } catch (err) {
      console.error("Fetch all configured apps error:", err);
    }
  };

  const applyFilters = () => {
    let result = [...orders];

    // DATE FILTER
    if (!showAllDates && fromDate && toDate) {
      const from = formatDateForAPI(fromDate);
      const to = formatDateForAPI(toDate);

      result = result.filter((o) => {
        const d = getLocalDateString(o.createdAt, timezone);
        return d >= from && d <= to;
      });
    }

    // STATUS FILTER
    const statusValue =
      typeof status === "object" && status?.value !== undefined
        ? status.value
        : status;
    if (
      statusValue !== "" &&
      statusValue !== null &&
      statusValue !== undefined
    ) {
      result = result.filter(
        (o) => o.status?.toLowerCase() === String(statusValue).toLowerCase(),
      );
    }

    // ORDER TYPE FILTER
    const orderTypeValue =
      typeof orderType === "object" && orderType?.value !== undefined
        ? orderType.value
        : orderType;
    if (
      orderTypeValue !== "" &&
      orderTypeValue !== null &&
      orderTypeValue !== undefined
    ) {
      const normalizedFilter = normalizeOrderType(orderTypeValue);
      result = result.filter((o) => {
        const normalizedOrder = normalizeOrderType(o.orderType);
        return normalizedOrder === normalizedFilter;
      });
    }

    // SEARCH FILTER
    // SEARCH FILTER
    if (search) {
      const s = search.trim().toLowerCase();

      result = result.filter((o) => {
        const orderId = o.orderId?.toString().toLowerCase() || "";
        const phone = o.phoneNumber?.toString().toLowerCase() || "";
        const billNo = o.billNumber?.toString().toLowerCase() || "";

        return (
          orderId.includes(s) ||
          phone.includes(s) ||
          billNo.includes(s)
        );
      });
    }


    setFiltered(result);
  };

  // Generate aggregated data by order type (date range filter only)
  const getAggregatedData = () => {
    const aggregated = {};

    // 1. Pre-fill standard internal order types
    const standardInternalTypes = ["Dine In", "Takeaway", "Delivery", "QR Order"];
    standardInternalTypes.forEach((type) => {
      aggregated[type] = { orderType: type, totalOrders: 0, totalSales: 0 };
    });

    // Apply only date filter and completed status, ignore other filters
    let dataToAggregate = [...orders].filter(
      (o) => o.status && o.status.toLowerCase() === "completed"
    );

    if (!showAllDates && fromDate && toDate) {
      const from = formatDateForAPI(fromDate);
      const to = formatDateForAPI(toDate);

      dataToAggregate = dataToAggregate.filter((o) => {
        const d = getLocalDateString(o.createdAt, timezone);
        return d >= from && d <= to;
      });
    }

    dataToAggregate.forEach((order) => {
      const type = formatOrderType(order.orderType);
      if (!aggregated[type]) {
        aggregated[type] = {
          orderType: type,
          totalOrders: 0,
          totalSales: 0,
        };
      }
      aggregated[type].totalOrders += 1;
      aggregated[type].totalSales += Number(order.amount || 0);
    });

    // Add "Other Apps" rows
    const otherAppsAgg = {};

    // Pre-fill all configured other apps
    allConfiguredApps.forEach((app) => {
      const appName = app?.name?.trim();
      if (appName) {
        otherAppsAgg[appName] = {
          orderType: appName,
          totalOrders: 0,
          totalSales: 0,
          isOtherApp: true,
        };
      }
    });

    if (otherAppsDailySales?.length) {
      otherAppsDailySales.forEach((sale) => {
        const appName = sale?.appName || "";
        if (!appName) return;

        // Display the saved app name as-is (e.g. "Swiggy")
        const rowKey = appName.toString().trim();
        // Total orders are now tracked per daily sale record
        if (!otherAppsAgg[rowKey]) {
          otherAppsAgg[rowKey] = {
            orderType: rowKey,
            totalOrders: 0,
            totalSales: 0,
            isOtherApp: true,
          };
        }

        otherAppsAgg[rowKey].totalOrders += Number(sale?.totalOrders || 0);
        otherAppsAgg[rowKey].totalSales += Number(sale?.totalSale || 0);
      });
    }

    return [...Object.values(aggregated), ...Object.values(otherAppsAgg)];
  };

  return {
    // State
    orders,
    filtered,
    otherAppsDailySales,
    isLoading,
    search,
    setSearch,
    status,
    setStatus,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    showAllDates,
    setShowAllDates,
    orderType,
    setOrderType,
    orderTypeOptions,

    // Computed data
    aggregatedData: getAggregatedData(),
    otherAppsTotalSale: (otherAppsDailySales || []).reduce(
      (sum, s) => sum + Number(s?.totalSale || 0),
      0,
    ),

    // Actions
    refetchOrders: fetchOrders,
  };
};
